import { set } from '@ember/object';
import type Owner from '@ember/owner';
import { setOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { task, type TaskInstance } from 'ember-concurrency';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type {
  BreakdownData,
  BreakdownItem,
  BreakdownResponse,
  HistoricalData,
  RouteQueryParams,
} from 'lumen/routes/market/parity/insight';
import type {
  MealTypeValues,
  MembershipType,
} from 'lumen/utils/constants/general-constants';
import type {
  CountryCodeWithDefault,
  DateAggregation,
  RateType,
} from 'lumen/utils/constants/pi-constant-values';
import {
  HISTORICAL_POINTS_AMOUNT,
  SINGLE_SELECT_BREAKDOWNS,
} from 'lumen/utils/constants/pi-constant-values';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import { demoTransformBreakdownData } from 'lumen/utils/parity/insight/demo-transform';
import { calculateParityAnalyticsValues } from 'lumen/utils/parity-analytics/calculate-parity-analytics-values';
import SilentTaskError from 'lumen/utils/silent-task-error';
import { hash } from 'rsvp';

type ParityAnalyticsRequestParams = (
  | { subscription_id: string }
  | { market_id: string }
) & {
  base_membership_type?: MembershipType;
  date_aggregation: DateAggregation;
  extract_date: string;
  filters?: string;
  historical_extract_date?: string;
  los: number;
  mealtype: MealTypeValues;
  periods?: number;
  pos: CountryCodeWithDefault;
  rate_type?: RateType;
};

import { MEMBERSHIPTYPES } from 'lumen/utils/constants/general-constants';

export default class DataFetching {
  @service declare ajax: Services['ajax'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  @tracked filteredCache: BreakdownData | null = null;
  @tracked filteredHistoricalCache: HistoricalData | null = null;
  @tracked unfilteredCache: BreakdownData | null = null;
  @tracked unfilteredHistoricalCache: HistoricalData | null = null;

  #isSingleProperty: boolean;

  requestFilteredHistoricalDataTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const requestParams: ParityAnalyticsRequestParams = {
          ...this.requestParams,
          filters: params.segmentationFilters,
          periods: HISTORICAL_POINTS_AMOUNT,
          extract_date: params.historicalExtractDate,
          date_aggregation: params.dateAggregation,
          rate_type: params.rateType,
          los: params.los,
          pos: params.pos,
          mealtype: params.mealPlan,
        };

        if (params.showMemberRate) {
          requestParams.base_membership_type = MEMBERSHIPTYPES.MEMBER_ONLY;
        }

        const filteredHistoricalData: HistoricalData = await this.ajax.request(
          formatUrl(this.apiPath, requestParams, this.ajax.host),
          {
            method: 'GET',
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
          },
        );
        set(this, 'filteredHistoricalCache', filteredHistoricalData);

        return filteredHistoricalData;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  requestFilteredSegmentationDataTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const requestParams: ParityAnalyticsRequestParams = {
          ...this.requestParams,
          filters: params.segmentationFilters,
          extract_date: params.extractDate,
          date_aggregation: params.dateAggregation,
          rate_type: params.rateType,
          los: params.los,
          pos: params.pos,
          mealtype: params.mealPlan,
        };

        if (params.showMemberRate) {
          requestParams.base_membership_type = MEMBERSHIPTYPES.MEMBER_ONLY;
        }

        const response: BreakdownResponse = await this.ajax.request(
          formatUrl(this.apiPath, requestParams, this.ajax.host),
          {
            method: 'GET',
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
          },
        );

        const latest = this.getLatestOnly(response);
        if (this.hotelAndMarketService.isDemoModeActive) {
          demoTransformBreakdownData(latest);
        }
        return latest;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  requestSegmentationDataFromCacheTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const filteredSegmentationData =
          await this.requestFilteredSegmentationDataTask.perform(params);
        const mergedResponses = this.mergeResponses(
          filteredSegmentationData,
          this.unfilteredCache as BreakdownData,
        );
        const mergedSegmentationData =
          this.handleSegmentationParentsAndChildren(mergedResponses);
        set(this, 'filteredCache', mergedSegmentationData);

        return mergedSegmentationData;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  requestSegmentationDataTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const allRequests: {
          filteredSegmentations?: TaskInstance<BreakdownData>;
          unfilteredSegmentations: TaskInstance<BreakdownData>;
        } = {
          unfilteredSegmentations:
            this.requestUnfilteredSegmentationDataTask.perform(params),
        };

        if (this.hasActiveFilters(params)) {
          allRequests.filteredSegmentations =
            this.requestFilteredSegmentationDataTask.perform(params);
        }

        const { filteredSegmentations, unfilteredSegmentations } =
          await hash(allRequests);
        const mergedResponses = this.mergeResponses(
          filteredSegmentations,
          unfilteredSegmentations,
        );

        // Always save the unfiltered response (always present) in the cache
        const unfilteredSegmentationData =
          this.handleSegmentationParentsAndChildren(unfilteredSegmentations);
        set(this, 'unfilteredCache', unfilteredSegmentationData);

        const filteredSegmentationData =
          this.handleSegmentationParentsAndChildren(mergedResponses);
        // If filters were active save the merged response as the filtered cache
        if (this.hasActiveFilters(params)) {
          set(this, 'filteredCache', filteredSegmentationData);
        }

        return filteredSegmentationData;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  requestUnfilteredHistoricalDataTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const requestParams: ParityAnalyticsRequestParams = {
          ...this.requestParams,
          periods: HISTORICAL_POINTS_AMOUNT,
          extract_date: params.historicalExtractDate,
          date_aggregation: params.dateAggregation,
          rate_type: params.rateType,
          los: params.los,
          pos: params.pos,
          mealtype: params.mealPlan,
        };

        if (params.showMemberRate) {
          requestParams.base_membership_type = MEMBERSHIPTYPES.MEMBER_ONLY;
        }

        const unfilteredHistoricalData: HistoricalData =
          await this.ajax.request(
            formatUrl(this.apiPath, requestParams, this.ajax.host),
            {
              method: 'GET',
              dataType: 'json',
              contentType: 'application/json; charset=utf-8',
            },
          );
        set(this, 'unfilteredHistoricalCache', unfilteredHistoricalData);

        return unfilteredHistoricalData;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  requestUnfilteredSegmentationDataTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const requestParams: ParityAnalyticsRequestParams = {
          ...this.requestParams,
          extract_date: params.extractDate,
          date_aggregation: params.dateAggregation,
          rate_type: params.rateType,
          los: params.los,
          pos: params.pos,
          mealtype: params.mealPlan,
        };

        if (params.showMemberRate) {
          requestParams.base_membership_type = MEMBERSHIPTYPES.MEMBER_ONLY;
        }

        const response: BreakdownResponse = await this.ajax.request(
          formatUrl(this.apiPath, requestParams, this.ajax.host),
          {
            method: 'GET',
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
          },
        );

        const latest = this.getLatestOnly(response);
        if (this.hotelAndMarketService.isDemoModeActive) {
          demoTransformBreakdownData(latest);
        }
        return latest;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get apiPath() {
    return this.#isSingleProperty
      ? '/apigateway/v1/app/parityanalytics/api/single_property'
      : '/apigateway/v1/app/parityanalytics/api/';
  }

  get requestParams(): { subscription_id: string } | { market_id: string } {
    if (this.#isSingleProperty) {
      return {
        subscription_id: this.hotelAndMarketService.hotelOrMarket.id!,
      };
    }

    return {
      market_id: this.hotelAndMarketService.hotelOrMarket.id!,
    };
  }

  constructor(owner: Owner, params: { isSingleProperty: boolean }) {
    setOwner(this, owner);
    this.#isSingleProperty = params.isSingleProperty;
  }

  clearCachedData(): void {
    this.filteredCache = null;
    this.filteredHistoricalCache = null;
    this.unfilteredCache = null;
    this.unfilteredHistoricalCache = null;
  }

  // Converts segmentation responses with only 1 period into a classic segmentation response:
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getLatestOnly(response: BreakdownResponse) {
    const classicResponse: Partial<BreakdownData> = {};

    // Search all segmentations in response
    (Object.keys(response) as (keyof BreakdownResponse)[]).forEach(
      (segmentation) => {
        // New response gives you one period per segmentation, in this function we only want to consider to most recent
        const latestPeriod = Object.keys(response[segmentation]).sort(
          (a, b) => dayjs.utc(a).unix() - dayjs.utc(b).unix(),
        )[0] as string;

        // A classic response only has this latest period as the data directly on its segmentation:
        classicResponse[segmentation] = response[segmentation][latestPeriod];
      },
    );

    return classicResponse as BreakdownData;
  }

  // For a Segmentation Object: fixes its parent/children relations, translations, and the creation of the 'other' row
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleSegmentationParentsAndChildren(model: BreakdownData) {
    const objSegmentationsById: Partial<
      Record<keyof BreakdownData, Record<string, BreakdownItem>>
    > = {};

    const breakdownKeys = Object.keys(model) as (keyof BreakdownData)[];
    breakdownKeys.forEach((segmentName) => {
      const segmentationObjects = model[segmentName];
      const childSegmentationObjects: BreakdownItem[] = [];

      objSegmentationsById[segmentName] = {};

      segmentationObjects.forEach((segmentationObject) => {
        const type = segmentationObject.segmentation_value_type;

        // This bit calculates values using the all total instead own value total, only used in a few blocks
        if (
          model.all.length &&
          model.all[0]?.value &&
          (segmentName === 'variance_bucket' || segmentName === 'issue_type')
        ) {
          // Variance calculated percentages are against the total number of shops for all segments!
          const absoluteTotal =
            model.all[0].value.win +
            model.all[0].value.meet +
            model.all[0].value.loss;
          set(
            segmentationObject,
            'calculated_values',
            calculateParityAnalyticsValues(
              segmentationObject.value,
              absoluteTotal,
            ),
          );
        } else {
          set(
            segmentationObject,
            'calculated_values',
            calculateParityAnalyticsValues(segmentationObject.value),
          );
        }

        if (type === 'parent') {
          // Empty the children array (prevent multiple appending of same children)
          set(segmentationObject, 'children', []);
          set(segmentationObject, 'isExpanded', false);
        }

        set(segmentationObject, 'isSelectable', true);

        if (type === 'child' && segmentationObject.parent_id) {
          childSegmentationObjects.push(segmentationObject);
        }

        if (objSegmentationsById[segmentName]) {
          objSegmentationsById[segmentName][segmentationObject.id] =
            segmentationObject;
        }
      });

      if (childSegmentationObjects.length) {
        childSegmentationObjects.forEach((childSegmentationObject) => {
          const { id } = childSegmentationObject;
          const parentId = childSegmentationObject.parent_id;

          if (parentId && objSegmentationsById[segmentName]?.[parentId]) {
            if (objSegmentationsById[segmentName][parentId].children) {
              objSegmentationsById[segmentName][parentId].children.push(
                childSegmentationObject,
              );
            } else {
              objSegmentationsById[segmentName][parentId].children = [];
              objSegmentationsById[segmentName][parentId].children.push(
                childSegmentationObject,
              );
            }

            delete objSegmentationsById[segmentName][id];
          }
        });
      }
    });

    const result: Partial<BreakdownData> = {};

    (Object.keys(objSegmentationsById) as (keyof BreakdownData)[]).forEach(
      (key) => {
        result[key] = Object.keys(
          objSegmentationsById[key] as Record<string, BreakdownItem>,
        ).map(
          (segmentationValueKey) =>
            objSegmentationsById[key]?.[segmentationValueKey] as BreakdownItem,
        );
      },
    );

    return result as BreakdownData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  hasActiveFilters(params: RouteQueryParams) {
    return Boolean(params.segmentationFilters);
  }

  /**
   * Merges two responses into one response. Mainly used to merge filtered/unfiltered or with cache to fill up gaps
   * This merging is especially necessary because filtered responses only contain those values that are different for that request
   * The values that did not change appear as undefined, and are then filled up by this function.
   * It is important to respect the order of arguments. Unfiltered or cache should come as a second argument.
   * ⚠️ When either of the arguments is not defined for some reason, the defined data is passed through
   * @param  {Object} filteredResponse   Segmentation Object from the filtered response
   * @param  {Object} unfilteredResponse Segmentation Object from the unfiltered response or cache
   * @return {Object}                    Segmentation Object as a consolidation of the two (OR)
   */
  mergeResponses(
    filteredResponse: BreakdownData | undefined,
    unfilteredResponse: BreakdownData,
  ): BreakdownData {
    // Make mergeResponses aware that it's not always going to be both filtered and unfiltered responses
    // So if either is missing, the other is passed along. If you call without any arguments, this will resolve without any response (of course, use it correctly!)
    if (!filteredResponse) {
      return unfilteredResponse;
    }

    if (!unfilteredResponse) {
      return filteredResponse;
    }

    (Object.keys(filteredResponse) as (keyof BreakdownData)[]).forEach(
      (filteredKey) => {
        // filteredKey = channel names. These are the same for the filtered & unfiltered response
        const filteredSegmentationArr = filteredResponse[filteredKey];
        // We get the unfiltered data with a default empty array to prevent breaking app. Sometimes API hides blocks with only one value.
        const unfilteredSegmentationArr =
          unfilteredResponse?.[filteredKey] ?? [];

        // For SINGLE_SELECT_BREAKDOWNS we only want to show what the API returns (like RI pickup block)
        // This makes sure we don't show empty values
        if (
          (SINGLE_SELECT_BREAKDOWNS as (keyof BreakdownData)[]).includes(
            filteredKey,
          )
        ) {
          return;
        }

        unfilteredSegmentationArr.forEach((unfilteredSegmentationObj) => {
          const unfilteredSegmentationObjId = unfilteredSegmentationObj.id;
          let foundInFilteredResult = false;

          filteredSegmentationArr.forEach((filteredSegmentationObj) => {
            if (
              filteredSegmentationObj.unfiltered_id ===
              unfilteredSegmentationObjId
            ) {
              filteredSegmentationObj.unfiltered_data =
                unfilteredSegmentationObj;

              foundInFilteredResult = true;
            }
          });

          if (!foundInFilteredResult) {
            // if unfiltered id is not found in the filtered response, add it with all zero values
            const dummyNotFoundInFilteredObj: BreakdownItem = {
              segmentation_value_id:
                unfilteredSegmentationObj.segmentation_value_id,
              value: {
                win: 0,
                meet: 0,
                loss: 0,
              },
              unfiltered_data: {
                value: unfilteredSegmentationObj.value,
                calculated_values: calculateParityAnalyticsValues(
                  unfilteredSegmentationObj.value,
                ),
              },
              segmentation_value: unfilteredSegmentationObj.segmentation_value,
              segmentation_id: unfilteredSegmentationObj.segmentation_id,
              segmentation_value_type:
                unfilteredSegmentationObj.segmentation_value_type,
              id: unfilteredSegmentationObjId,
              meta: unfilteredSegmentationObj.meta,
            };

            filteredSegmentationArr.push(dummyNotFoundInFilteredObj);
          }
        });
      },
    );

    // Return the adapted filtered response
    return filteredResponse;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  requestHistoricalDataTask(params: RouteQueryParams) {
    if (this.hasActiveFilters(params)) {
      return this.requestFilteredHistoricalDataTask.perform(params);
    }

    return this.requestUnfilteredHistoricalDataTask.perform(params);
  }
}
